import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Container } from '../../layout';
import { buttonVariants } from '../../ui/Button';

export interface HeroSectionProps {
  /**
   * Custom class name for the hero section
   */
  className?: string;
}

/**
 * HeroSection component for the homepage
 * Full viewport height hero with gradient background and CTAs
 */
export const HeroSection: React.FC<HeroSectionProps> = ({ className }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      className={cn(
        'relative min-h-screen flex items-center overflow-hidden',
        // Gradient background from teal-50 to white
        'bg-gradient-to-br from-primary-50 via-white to-secondary-50/30',
        'dark:from-slate-900 dark:via-slate-900 dark:to-slate-800',
        className
      )}
      aria-labelledby="hero-heading"
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {/* Top right circle */}
        <div
          className={cn(
            'absolute -top-24 -right-24 w-96 h-96 rounded-full',
            'bg-gradient-to-br from-primary-200/40 to-primary-100/20',
            'blur-3xl transition-opacity duration-1000',
            isVisible ? 'opacity-100' : 'opacity-0'
          )}
        />
        {/* Bottom left circle */}
        <div
          className={cn(
            'absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full',
            'bg-gradient-to-tr from-secondary-200/30 to-secondary-100/10',
            'blur-3xl transition-opacity duration-1000 delay-200',
            isVisible ? 'opacity-100' : 'opacity-0'
          )}
        />
        {/* Center accent */}
        <div
          className={cn(
            'absolute top-1/2 left-1/3 w-64 h-64 rounded-full',
            'bg-gradient-to-br from-primary-100/20 to-transparent',
            'blur-2xl transition-opacity duration-1000 delay-300',
            isVisible ? 'opacity-100' : 'opacity-0'
          )}
        />
        {/* Small decorative dots */}
        <div className="absolute top-20 left-20 w-2 h-2 bg-primary-300 rounded-full opacity-60" />
        <div className="absolute top-40 right-40 w-3 h-3 bg-secondary-300 rounded-full opacity-40" />
        <div className="absolute bottom-40 left-1/4 w-2 h-2 bg-primary-400 rounded-full opacity-50" />
        <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 bg-secondary-400 rounded-full opacity-40" />
      </div>

      <Container size="xl" className="relative z-10 py-16 md:py-20 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content Section */}
          <div
            className={cn(
              'max-w-2xl transition-all duration-700 ease-out',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            {/* Badge/Label */}
            <div
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-full',
                'backdrop-blur-sm bg-primary-100/80 border border-primary-200/50',
                'dark:bg-primary-900/50 dark:border-primary-700/50',
                'text-primary-700 dark:text-primary-300 text-sm font-medium',
                'mb-6 transition-all duration-500 delay-100',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              )}
            >
              <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
              Professional Massage Therapy
            </div>

            {/* Main Headline */}
            <h1
              id="hero-heading"
              className={cn(
                'font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl',
                'font-bold text-gray-900 dark:text-slate-100 leading-tight tracking-tight',
                'mb-6 transition-all duration-700 delay-200',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              )}
            >
              Experience the Art of{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-500">
                Therapeutic Massage
              </span>
            </h1>

            {/* Subheadline */}
            <p
              className={cn(
                'text-lg sm:text-xl text-gray-600 dark:text-slate-400 leading-relaxed',
                'mb-8 max-w-xl transition-all duration-700 delay-300',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              )}
            >
              Discover personalized massage therapy sessions designed to restore your body, calm
              your mind, and rejuvenate your spirit. Our certified therapists bring years of
              expertise to every treatment.
            </p>

            {/* CTA Buttons */}
            <div
              className={cn(
                'flex flex-col sm:flex-row gap-4',
                'transition-all duration-700 delay-400',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              )}
            >
              <Link
                to="/book"
                className={cn(
                  buttonVariants({ variant: 'primary', size: 'lg' }),
                  'shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-shadow'
                )}
              >
                <Calendar className="w-5 h-5" aria-hidden="true" />
                Book Your Session
              </Link>
              <Link
                to="/services"
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'lg' }),
                  'border-gray-300 hover:border-primary-300 hover:bg-primary-50/50',
                  'dark:border-slate-600 dark:hover:border-primary-500 dark:hover:bg-primary-900/30'
                )}
              >
                View Services
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            </div>

            {/* Trust Indicators */}
            <div
              className={cn(
                'mt-10 pt-8 border-t border-gray-200 dark:border-slate-700',
                'transition-all duration-700 delay-500',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              )}
            >
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-primary-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Licensed Therapists</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-primary-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Flexible Booking</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-primary-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Secure Payments</span>
                </div>
              </div>
            </div>
          </div>

          {/* Image/Visual Section */}
          <div
            className={cn(
              'relative',
              'transition-all duration-1000 delay-300',
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            )}
          >
            {/* Image placeholder container */}
            <div className="relative mx-4 sm:mx-0">
              {/* Main image placeholder */}
              <div
                className={cn(
                  'relative z-10 rounded-2xl overflow-hidden',
                  'bg-gradient-to-br from-primary-100 to-secondary-100',
                  'aspect-[3/2] sm:aspect-[4/3] lg:aspect-[4/5] w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto',
                  'shadow-2xl shadow-gray-900/10 dark:shadow-slate-900/30',
                  'border border-white/30'
                )}
              >
                {/* Placeholder content - replace with actual image */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-white/50 flex items-center justify-center">
                      <svg
                        className="w-12 h-12 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-primary-700 font-medium">Wellness & Relaxation</p>
                    <p className="text-primary-600/70 text-sm mt-1">Image placeholder</p>
                  </div>
                </div>
              </div>

              {/* Decorative elements around image */}
              <div
                className={cn(
                  'absolute -top-4 -right-4 w-24 h-24 rounded-xl',
                  'bg-secondary-500 opacity-80',
                  'transform rotate-12',
                  'hidden sm:block'
                )}
              />
              <div
                className={cn(
                  'absolute -bottom-6 -left-6 w-32 h-32 rounded-2xl',
                  'bg-primary-500 opacity-80',
                  'hidden sm:block'
                )}
              />

              {/* Floating card element */}
              <div
                className={cn(
                  'absolute -bottom-4 right-2 sm:-right-8 z-20',
                  'backdrop-blur-lg bg-white/90 dark:bg-slate-800/90 border border-white/30 dark:border-slate-700/50',
                  'rounded-xl shadow-lg p-4',
                  'flex items-center gap-3',
                  'transition-all duration-700 delay-700',
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
              >
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-slate-100">500+</p>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Happy Clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
        <svg
          className="w-full h-16 sm:h-24 text-white dark:text-slate-900"
          viewBox="0 0 1440 74"
          fill="currentColor"
          preserveAspectRatio="none"
        >
          <path d="M0,37 C240,74 480,0 720,37 C960,74 1200,0 1440,37 L1440,74 L0,74 Z" />
        </svg>
      </div>
    </section>
  );
};

HeroSection.displayName = 'HeroSection';
